import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.#loadVideo();
            this.#intersectionObserver?.disconnect();
            this.#intersectionObserver = null;
          }
        },
        { rootMargin: '100px' }
      );

      this.#intersectionObserver.observe(this);
      return;
    }

    this.#loadVideo();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
  }

  #loadVideo() {
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource && !source.getAttribute('src')) {
        source.setAttribute('src', videoSource);
      }
    }

    videoElement.load();
    videoElement.play().catch(() => {});
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
